import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import type { Result } from '@alpha-edge/shared'
import { useApiEntity } from '../../hooks/use-api-entity'
import { useApiList } from '../../hooks/use-api-list'
import { useModal } from '../../hooks/use-modal'
import { api } from '../../services/api'
import {
  PageHeader,
  FormField,
  Alert,
  Modal,
  ConfirmDialog,
  DataTable,
  StatusBadge,
  btnPrimary,
  btnSecondary,
  btnDanger,
  card,
  input,
  colors,
} from '../../components/ui'
import type { Column } from '../../components/ui'

interface DeviceForm {
  serialNumber: string
  name: string
  deviceType: string
  manufacturer: string
  model: string
  firmwareVersion: string
  status: string
  lastCalibrationAt: string
  nextCalibrationDue: string
}

const defaults: DeviceForm = {
  serialNumber: '',
  name: '',
  deviceType: '',
  manufacturer: '',
  model: '',
  firmwareVersion: '',
  status: 'active',
  lastCalibrationAt: '',
  nextCalibrationDue: '',
}

const resultColumns: readonly Column<Result>[] = [
  { key: 'barcode', header: 'Barcode', render: (r) => r.barcode || '--' },
  { key: 'testType', header: 'Test Type', render: (r) => r.testType },
  { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  { key: 'value', header: 'Value', render: (r) => r.resultValue || '--' },
  { key: 'date', header: 'Date', render: (r) => new Date(r.createdAt).toLocaleDateString() },
]

export function DeviceEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data, isNew, loading, saving, error, success, save, cancel, update, setError, refresh } =
    useApiEntity<DeviceForm>({
      path: '/devices',
      listPath: '/devices',
      defaultValue: defaults,
    })

  const calibrationModal = useModal()
  const archiveModal = useModal()
  const [nextDue, setNextDue] = useState('')
  const [calibrating, setCalibraging] = useState(false)
  const [archiving, setArchiving] = useState(false)

  const { data: results, loading: resultsLoading } = useApiList<Result>({
    path: isNew ? '' : `/devices/${id}/results`,
    limit: 10,
  })

  const handleCalibration = async () => {
    setCalibraging(true)
    try {
      await api.post(`/devices/${id}/calibration`, { nextDue: nextDue || undefined })
      calibrationModal.close()
      setNextDue('')
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record calibration')
    } finally {
      setCalibraging(false)
    }
  }

  const handleArchive = async () => {
    setArchiving(true)
    try {
      await api.delete(`/devices/${id}`)
      navigate('/devices')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive device')
      archiveModal.close()
    } finally {
      setArchiving(false)
    }
  }

  if (loading) return <div style={{ color: colors.textMuted }}>Loading...</div>

  return (
    <div>
      <PageHeader
        title={isNew ? 'New Device' : data.name}
        backTo="/devices"
        backLabel="Back to Devices"
        actions={
          <>
            {!isNew && (
              <button onClick={calibrationModal.open} style={btnPrimary}>
                Record Calibration
              </button>
            )}
            {!isNew && (
              <button onClick={archiveModal.open} style={btnDanger}>
                Archive
              </button>
            )}
            <button onClick={cancel} style={btnSecondary}>
              Cancel
            </button>
            <button onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </>
        }
      />
      {error && <Alert type="error" message={error} />}
      {success && <Alert type="success" message={success} />}

      <div style={{ ...card, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <FormField
          name="serialNumber"
          label="Serial Number"
          required
          value={data.serialNumber}
          onChange={(v) => update('serialNumber', v)}
          disabled={!isNew}
        />
        <FormField name="name" label="Name" required value={data.name} onChange={(v) => update('name', v)} />
        <FormField
          name="deviceType"
          label="Device Type"
          required
          value={data.deviceType}
          onChange={(v) => update('deviceType', v)}
        />
        <FormField
          name="manufacturer"
          label="Manufacturer"
          value={data.manufacturer}
          onChange={(v) => update('manufacturer', v)}
        />
        <FormField name="model" label="Model" value={data.model} onChange={(v) => update('model', v)} />
        <FormField
          name="firmwareVersion"
          label="Firmware Version"
          value={data.firmwareVersion}
          onChange={(v) => update('firmwareVersion', v)}
        />
        <FormField name="status" label="Status">
          <select style={input} value={data.status} onChange={(e) => update('status', e.target.value)}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="maintenance">Maintenance</option>
            <option value="retired">Retired</option>
          </select>
        </FormField>
        {!isNew && (
          <>
            <FormField
              name="lastCalibrationAt"
              label="Last Calibration"
              value={data.lastCalibrationAt ? new Date(data.lastCalibrationAt).toLocaleDateString() : '--'}
              disabled
            />
            <FormField
              name="nextCalibrationDue"
              label="Next Calibration Due"
              value={data.nextCalibrationDue ? new Date(data.nextCalibrationDue).toLocaleDateString() : '--'}
              disabled
            />
          </>
        )}
      </div>

      {!isNew && (
        <div style={{ ...card, marginTop: '1rem' }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', color: colors.text }}>Recent Results</h3>
          {resultsLoading ? (
            <div style={{ color: colors.textMuted }}>Loading results...</div>
          ) : (
            <DataTable
              columns={resultColumns}
              rows={results}
              keyField="id"
              onRowClick={(r) => navigate(`/results/${r.id}`)}
              emptyMessage="No results for this device."
            />
          )}
        </div>
      )}

      <Modal isOpen={calibrationModal.isOpen} onClose={calibrationModal.close} title="Record Calibration">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {data.lastCalibrationAt && (
            <p style={{ margin: 0, fontSize: '0.875rem', color: colors.textMuted }}>
              Last calibrated: {new Date(data.lastCalibrationAt).toLocaleString()}
            </p>
          )}
          <FormField name="nextDue" label="Next Calibration Due" type="date" value={nextDue} onChange={setNextDue} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <button style={btnSecondary} onClick={calibrationModal.close}>
              Cancel
            </button>
            <button style={btnPrimary} onClick={handleCalibration} disabled={calibrating}>
              {calibrating ? 'Saving...' : 'Record Calibration'}
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={archiveModal.isOpen}
        onClose={archiveModal.close}
        onConfirm={handleArchive}
        title="Archive Device"
        message={`Are you sure you want to archive ${data.name}? It will be excluded from search results.`}
        confirmLabel="Archive"
        loading={archiving}
      />
    </div>
  )
}
